import { type FC, useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Typography, Button, Space, Card, Spin } from 'antd';
import { ArrowRightOutlined, DownloadOutlined } from '@ant-design/icons';
import { ResumeModal, downloadResume } from '@/components/ResumeModal';
import { SEO } from '@/components/SEO';
import { Hero } from '@/components/Hero';
import { ProjectCard } from '@/components/ProjectCard';
import { BlogCard } from '@/components/BlogCard';
import { TestimonialCard } from '@/components/TestimonialCard';
import { UpworkCard } from '@/components/UpworkCard';
import { StatCard } from '@/components/StatCard';
import { useSiteContent, useProjects, useBlogPosts, useTestimonials } from '@/context/ContentProvider';
import { useScrollTracking } from '@/hooks/useScrollTracking';
import { trackCTAClick } from '@/utils/analytics';

const { Title, Paragraph } = Typography;

export const Home: FC = () => {
  const [resumeOpen, setResumeOpen] = useState(false);
  useScrollTracking('home');
  const { home } = useSiteContent();
  const { projects, loading: projectsLoading } = useProjects();
  const { blogPosts, loading: blogLoading } = useBlogPosts();
  const { testimonials, loading: testimonialsLoading } = useTestimonials();

  const featuredProjects = useMemo(
    () => projects.filter((p) => p.featured).slice(0, 3),
    [projects],
  );
  const recentPosts = useMemo(
    () => [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 2),
    [blogPosts],
  );

  const loading = projectsLoading || blogLoading || testimonialsLoading;

  // Restore the exact scroll position the user had before opening a project
  // detail page from a featured card — they land right where they left off.
  //
  // Back-navigation runs inside a View Transition: after the DOM swap, the
  // browser resets scroll to its position on the destination page (0). To win
  // that race we apply the saved position synchronously AND across the next
  // two frames, so we still hold the scroll after the transition settles.
  // `history.scrollRestoration = 'manual'` does not prevent this reset.
  useEffect(() => {
    if (projectsLoading) return;
    const saved = sessionStorage.getItem('projects-restore-scroll');
    if (saved === null) return;
    const y = Number(saved);
    if (Number.isNaN(y)) return;

    const apply = () => window.scrollTo({ top: y, behavior: 'instant' });
    apply();
    const raf1 = requestAnimationFrame(() => {
      apply();
      const raf2 = requestAnimationFrame(apply);
      (apply as unknown as { _raf2?: number })._raf2 = raf2;
    });

    const removeFlag = setTimeout(() => {
      sessionStorage.removeItem('projects-restore-scroll');
    }, 150);

    return () => {
      cancelAnimationFrame(raf1);
      const raf2 = (apply as unknown as { _raf2?: number })._raf2;
      if (raf2) cancelAnimationFrame(raf2);
      clearTimeout(removeFlag);
    };
  }, [projectsLoading]);

  return (
    <>
      <SEO />

      <Hero />

      {/* Stats */}
      <section
        className="section-sm stats-section"
        style={{ borderTop: '1px solid var(--color-border)' }}
      >
        <div className="container">
          <Row gutter={[48, 40]} justify="center">
            {home.stats.map((stat) => (
              <Col key={stat.label} xs={12} md={6}>
                <StatCard
                  value={stat.value}
                  label={stat.label}
                  hint={stat.hint}
                  tooltip={stat.tooltip}
                  tooltipLink={stat.tooltipLink}
                />
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Featured Projects */}
      {loading ? (
        <section className="section"><div className="container" style={{ textAlign: 'center' }}><Spin size="large" /></div></section>
      ) : (
        <>
          {featuredProjects.length > 0 && (
            <section className="section">
              <div className="container">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    marginBottom: 48,
                    flexWrap: 'wrap',
                    gap: 16,
                  }}
                >
                  <div>
                    <Title level={2} className="section-title">
                      {home.featuredProjectsTitle}
                    </Title>
                    <Paragraph className="section-subtitle">
                      {home.featuredProjectsSubtitle}
                    </Paragraph>
                  </div>
                  <Link to="/portfolio">
                    <Button
                      type="link"
                      onClick={() => trackCTAClick('view_all_projects', 'home')}
                    >
                      {home.viewAllProjectsText} <ArrowRightOutlined />
                    </Button>
                  </Link>
                </div>
                <div className="project-grid project-grid--3col">
                  {featuredProjects.map((project) => (
                    <ProjectCard key={project.id} project={project} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Testimonials */}
          {testimonials.length > 0 && (
            <section className="section-sm">
              <div className="container">
                <div style={{ textAlign: 'center', marginBottom: 48 }}>
                  <Title level={2} className="section-title">
                    {home.testimonialsTitle}
                  </Title>
                  <Paragraph className="section-subtitle" style={{ margin: '0 auto' }}>
                    {home.testimonialsSubtitle}
                  </Paragraph>
                </div>
                <Row gutter={[24, 24]}>
                  {testimonials.map((t) => (
                    <Col key={t.id} xs={24} md={8}>
                      <TestimonialCard testimonial={t} />
                    </Col>
                  ))}
                </Row>
              </div>
            </section>
          )}

          {/* Upwork rating */}
          {home.upwork?.enabled !== false && (
            <section className="section-sm">
              <div className="container">
                <UpworkCard data={home.upwork} />
              </div>
            </section>
          )}

          {/* Latest Blog Posts */}
          {recentPosts.length > 0 && (
            <section className="section">
              <div className="container">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    marginBottom: 48,
                    flexWrap: 'wrap',
                    gap: 16,
                  }}
                >
                  <div>
                    <Title level={2} className="section-title">
                      {home.blogTitle}
                    </Title>
                    <Paragraph className="section-subtitle">
                      {home.blogSubtitle}
                    </Paragraph>
                  </div>
                  <Link to="/blog">
                    <Button
                      type="link"
                      onClick={() => trackCTAClick('view_all_blog', 'home')}
                    >
                      {home.viewAllBlogText} <ArrowRightOutlined />
                    </Button>
                  </Link>
                </div>
                <Row gutter={[24, 24]}>
                  {recentPosts.map((post) => (
                    <Col key={post.id} xs={24} md={12}>
                      <BlogCard post={post} compact />
                    </Col>
                  ))}
                </Row>
              </div>
            </section>
          )}
        </>
      )}

      {/* CTA Section */}
      <section className="section">
        <div className="container">
          <Card
            style={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(176,150,125,0.08), rgba(176,150,125,0.02))',
              border: '1px solid rgba(176,150,125,0.2)',
            }}
            styles={{ body: { padding: '64px 32px' } }}
          >
            <Title level={2} style={{ marginBottom: 12 }}>
              {home.ctaTitle}
            </Title>
            <Paragraph
              type="secondary"
              style={{ fontSize: 17, marginBottom: 32, maxWidth: 480, margin: '0 auto 32px' }}
            >
              {home.ctaSubtitle}
            </Paragraph>
            <Space size={16} wrap style={{ justifyContent: 'center' }}>
              <Link to="/contact">
                <Button
                  type="primary"
                  size="large"
                  onClick={() => trackCTAClick('get_in_touch', 'home_cta')}
                  style={{ height: 48, paddingInline: 28, fontWeight: 600 }}
                >
                  {home.ctaButtonText}
                </Button>
              </Link>
              <Button
                size="large"
                icon={<DownloadOutlined />}
                onClick={() => {
                  downloadResume();
                  trackCTAClick('resume_download', 'home_cta');
                  setResumeOpen(true);
                }}
                style={{ height: 48, paddingInline: 28 }}
              >
                {home.ctaResumeText}
              </Button>
            </Space>
          </Card>
          <ResumeModal open={resumeOpen} onClose={() => setResumeOpen(false)} />
        </div>
      </section>
    </>
  );
};
